// BCM-898 · Goods Receipt Dashboard — shared styles.
// Colours and layout match the mockups in BCM-898_Batch Dashboard Redesign.pptx.
import { makeStyles } from "@material-ui/core/styles";

export const ORANGE = "#E36E39";
export const BLUE = "#2F6FB0";

// The app's single global theme (src/index.jsx) only customises `palette` — it
// never overrides `typography.fontFamily`, so this IS the same default MUI stack
// the Batch (SASL) dashboard renders with. Exported so anything that renders
// through a React portal — Dialogs, Snackbars — can pin to it explicitly: portals
// attach to `document.body`, which is outside this dashboard's own DOM subtree and
// has no font-family of its own (CssBaseline isn't mounted app-wide), so any text
// not wrapped in an MUI Typography/Button/etc. would otherwise fall back to the
// browser's default (serif) font instead of matching the rest of the app.
export const GR_FONT_FAMILY = '"Roboto", "Helvetica", "Arial", sans-serif';

export const useGRStyles = makeStyles((theme) => ({
  // fontFamily here covers the whole embedded dashboard tree (table, action
  // boxes, banners, cards, tabs) via inheritance — everything else in this file
  // is a descendant of the single root div this class is applied to.
  root: { padding: "0.5rem 0", fontFamily: theme.typography.fontFamily },

  // ── filter bar ───────────────────────────────────────────────────────────
  lastSynced: { fontSize: 12, color: "#9AA3AF", paddingRight: 10, whiteSpace: "nowrap" },
  refresh: {
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
    fontSize: 12,
    color: ORANGE,
    fontStyle: "italic",
    "&:hover": { textDecoration: "underline" },
  },
  emptyState: {
    textAlign: "center",
    padding: "3rem 1rem",
    color: "#777",
    fontSize: "0.95rem",
  },

  // ── table ────────────────────────────────────────────────────────────────
  // View Info column icons (mockup): plain blue outline icons, no bordered box.
  grIconBtn: {
    color: BLUE,
    padding: 6,
    margin: "0 2px",
    "&:hover": { backgroundColor: "rgba(47, 111, 176, 0.08)" },
    "&:disabled": { color: "#B9C4D1" },
  },

  // ── action section shell ─────────────────────────────────────────────────
  section: { padding: "1.25rem 1.5rem", backgroundColor: "#FFFFFF" },
  sectionHeadRow: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem", marginBottom: "0.4rem" },
  sectionTitle: { fontWeight: 700, fontSize: "1.05rem", display: "flex", alignItems: "center", gap: 8, color: "#1B2430" },
  sectionSub: { color: "#6B7280", fontSize: "0.85rem", marginBottom: "1rem" },

  // ── peach information banner (PA1/PA7/PA12) ──────────────────────────────
  banner: { backgroundColor: "#FFF4EC", border: "1px solid #F6C9A8", borderRadius: 8, padding: "12px 16px", marginBottom: "1rem" },
  bannerTitle: { fontWeight: 700, fontSize: "0.95rem", color: "#C1440E", display: "flex", alignItems: "center", gap: 8 },
  bannerSub: { fontSize: "0.85rem", color: "#9A5B33", marginTop: 2 },
  bannerReminder: { fontSize: "0.8rem", color: "#9A5B33", marginTop: 8, display: "flex", alignItems: "center", gap: 6 },
  // Green success banner shown once every configured document is received.
  bannerSuccess: { backgroundColor: "#EAF7EC", border: "1px solid #BFE6C6", borderRadius: 8, padding: "12px 16px", marginBottom: "1rem" },
  bannerTitleSuccess: { fontWeight: 700, fontSize: "0.95rem", color: "#1E7B34", display: "flex", alignItems: "center", gap: 8 },
  bannerSubSuccess: { fontSize: "0.85rem", color: "#3B7A46", marginTop: 2 },

  // ── document status bars (PA1/PA7/PA12) ──────────────────────────────────
  docBar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderRadius: 8, marginBottom: 10, fontWeight: 600 },
  docBarReceived: { backgroundColor: "#EAF7EC", border: "1px solid #BFE6C6" },
  docBarPending: { backgroundColor: "#FDECEA", border: "1px solid #F5B7B1" },
  docName: { display: "flex", alignItems: "center", gap: 8, color: "#1B2430", fontWeight: 600 },
  statusReceived: { color: "#1E7B34", fontWeight: 700, fontSize: "0.8rem", letterSpacing: ".02em" },
  statusPending: { color: "#C0392B", fontWeight: 700, fontSize: "0.8rem", letterSpacing: ".02em" },

  // ── pink failure card + horizontal labelled fields (PA4/PA8/PA10/PA11) ───
  failCard: { backgroundColor: "#FDECEA", border: "1px solid #F5B7B1", borderRadius: 8, padding: "14px 18px", marginBottom: 12 },
  failCardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 },
  failCardTitle: { color: "#C0392B", fontWeight: 700, fontSize: "0.92rem", display: "flex", alignItems: "center", gap: 8 },

  // ── green "passed / success" variant of the same card (PA8 passed checks) ─
  passCard: { backgroundColor: "#EAF7EC", border: "1px solid #BFE6C6", borderRadius: 8, padding: "14px 18px", marginBottom: 12 },
  passCardTitle: { color: "#1E7B34", fontWeight: 700, fontSize: "0.92rem", display: "flex", alignItems: "center", gap: 8 },
  // A fixed 3-column grid (not flex-wrap) so fields line up in even columns
  // across cards, matching the mockup — flex-wrap's column widths drift with
  // each field's own content width instead of staying aligned row to row.
  // row-gap kept tight (col-gap unchanged) — only visible when a check has
  // multiple material rows (PA8's per-item checks); single-row cards
  // (PA4/PA10/PA11, PA8's PO-level checks) never wrap, so this is a no-op there.
  fieldGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.5rem 2rem" },
  // Titleless FieldCard + rightAction on one row (PA10/PA11 QA release card).
  // fieldGrid grows to fill the row and spreads its fields evenly before the button.
  fieldRowWithAction: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  fieldRowGrid: { display: "flex", flexWrap: "wrap", flex: 1, gap: "1.5rem 3rem", justifyContent: "space-between" },
  fieldLabel: { fontSize: "0.72rem", color: "#8A93A1", textTransform: "uppercase", letterSpacing: ".04em", fontWeight: 600 },
  fieldValue: { fontSize: "0.92rem", color: "#1B2430", fontWeight: 700, marginTop: 2 },
  fieldValueRed: { color: "#C0392B" },
  noteLine: { fontSize: "0.78rem", color: "#9A5B33", fontStyle: "italic", marginTop: 10 },
  // PA9's own Ready for Dispatch Qty / UoM / Shipping all quantity / Date of
  // Manufacture row - normal case, bold black label + normal-weight black value,
  // deliberately separate from fieldLabel/fieldValue (uppercase, grey label) above,
  // which 18 other places in this dashboard already use - this doesn't recolour them.
  pa9FieldLabel: { fontSize: "0.85rem", color: "#1B2430", fontWeight: 700 },
  pa9FieldValue: { fontSize: "0.9rem", color: "#1B2430", fontWeight: 400, marginTop: 2 },
  // ── check header badge (PA8) ─────────────────────────────────────────────
  badge: { backgroundColor: "transparent", color: "#C0392B", border: "1px solid #C0392B", borderRadius: 6, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700, marginLeft: 10, letterSpacing: ".03em" },
  badgeSuccess: { backgroundColor: "transparent", color: "#1E7B34", border: "1px solid #1E7B34", borderRadius: 6, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700, marginLeft: 10, letterSpacing: ".03em" },

  // ── tabs (PA2/PA4/PA6) ──────────────────────────────────────────────────
  // The bar is a fixed 50/50 split: scrollable tabs on the left, buttons on the
  // right. Nothing wraps to a second line — overflowing tabs get chevron arrows.
  tabsBar: { display: "flex", alignItems: "center", borderBottom: "1px solid #E0E4EA", marginBottom: "1rem", flexWrap: "nowrap", gap: 12 },
  tabsBarLeft: { display: "flex", alignItems: "center", width: "50%", flex: "0 0 50%", minWidth: 0 },
  tabsBarRight: { display: "flex", alignItems: "center", justifyContent: "flex-end", width: "50%", flex: "0 0 50%", minWidth: 0 },
  tabScroller: {
    display: "flex",
    gap: "1.5rem",
    flex: "1 1 auto",
    minWidth: 0,
    overflowX: "auto",
    scrollBehavior: "smooth",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
  tabArrow: {
    flex: "0 0 auto",
    color: "#fff",
    backgroundColor: BLUE,
    width: 32,
    height: 32,
    padding: 0,
    "&:hover": { backgroundColor: "#255A90" },
    "&:disabled": { backgroundColor: "#C7D2DE", color: "#fff" },
  },
  tab: { display: "flex", alignItems: "center", gap: 8, padding: "10px 2px", cursor: "pointer", fontWeight: 600, fontSize: "0.95rem", color: "#6B7280", borderBottom: "3px solid transparent", marginBottom: -1, flex: "0 0 auto", whiteSpace: "nowrap" },
  tabActive: { color: BLUE, borderBottomColor: BLUE },
  tabBadge: { backgroundColor: BLUE, color: "#fff", borderRadius: 999, minWidth: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.72rem", fontWeight: 700, padding: "0 6px" },

  // ── inline tables (PA3/PA6) ──────────────────────────────────────────────
  tableCellHead: { fontWeight: 700, backgroundColor: "#F3F5F8", fontSize: "0.78rem", color: "#1B2430" },
  errorLine: { backgroundColor: "#FDECEA", border: "1px solid #F5B7B1", borderRadius: 8, padding: "10px 14px", color: "#C0392B", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },

  // ── buttons ──────────────────────────────────────────────────────────────
  blueBtn: { backgroundColor: BLUE, color: "#FFFFFF", borderRadius: 6, padding: "6px 16px", textTransform: "none", fontWeight: 600, fontSize: "0.82rem", "&:hover": { backgroundColor: "#255A90" }, "&:disabled": { backgroundColor: "#B9C4D1", color: "#fff" } },
  orangeBtn: { backgroundColor: ORANGE, color: "#FFFFFF", borderRadius: 999, padding: "8px 32px", textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#C44D15" }, "&:disabled": { backgroundColor: "#E4C4B2", color: "#fff" } },
  outlinedBtn: { border: "1px solid #1B2430", color: "#1B2430", borderRadius: 999, padding: "7px 22px", textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#F3F5F8" } },
  // Confirmed / completed state of a primary action button (slides 10, 30): the
  // button stays in place and turns green, reading e.g. "Consumption Confirmed".
  // Rendered disabled, so the disabled colours must stay green — MUI would
  // otherwise grey it out and the confirmed state would read as "unavailable".
  confirmedBtn: { backgroundColor: "#2e7d32", color: "#FFFFFF", borderRadius: 999, padding: "8px 32px", textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: "#2e7d32" }, "&.Mui-disabled": { backgroundColor: "#2e7d32", color: "#FFFFFF" } },
  // "GRN Generated" completed state (slide 31 toast) - its own shade of green per
  // the design mock, kept separate from confirmedBtn so it doesn't also recolour
  // "Consumption Confirmed" etc. elsewhere in this dashboard.
  grnGeneratedBtn: { backgroundColor: "#499F2A", color: "#FFFFFF", borderRadius: 999, padding: "8px 32px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", "&:hover": { backgroundColor: "#499F2A" }, "&.Mui-disabled": { backgroundColor: "#499F2A", color: "#FFFFFF" } },
  // Forces the endIcon white even under the button's own Mui-disabled colour cascade -
  // the "&.Mui-disabled" rule above reaches the button's text but not the icon, so
  // htmlColor alone wasn't enough; !important guarantees this wins regardless.
  grnGeneratedIcon: { color: "#FFFFFF !important", opacity: "1 !important", fill: "#FFFFFF !important" },
  btnRow: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },

  depNote: { backgroundColor: "#FFFDE7", border: "1px dashed #E0C000", borderRadius: 6, padding: "8px 12px", fontSize: "0.78rem", color: "#7a6a00", margin: "8px 0" },
  viewOnlyNote: { color: "#C1440E", display: "block", marginBottom: 8, fontSize: "0.78rem" },
}));
